import React, { useState } from "react";
import { CardElement, injectStripe } from "react-stripe-elements";
import Spinner from "components/shared/Spinner/Spinner";
import {
    Input,
    Stack,
    Heading,
    Button,
    Modal,
    Alert,
    Container,
} from "@dgraph-io/typhoon-ui";

import { useAuth } from "libs/auth";
import trackEvent, { events } from "libs/trackevents";
import { useApplyPromo } from "gql/hooks/promo/promo.hook";
import { ApolloError } from "@apollo/client";
import { styles } from "./AddCard.styles";
import { ApplyPromo } from "gql/types/ApplyPromo";

const resetStatusModalState = {
    isOpen: false,
    message: "",
    heading: "",
};

function UpdateCard({
    stripe,
    onSuccess,
    onFailure,
    onCancel,
    onCreateSourceID,
}: {
    stripe?: any;
    onSuccess: () => void;
    onFailure: () => void;
    onCancel: () => void;
    onCreateSourceID: (sourceID: string) => void;
}) {
    const { user } = useAuth();
    const [error, setError] = useState(null);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [isCardUpdateLoading, setIsCardUpdateLoading] = useState(false);
    const [promoCode, setPromoCode] = useState("");
    const [couponStatusModal, setCouponStatusModal] = useState(
        resetStatusModalState
    );

    const { applyPromo, loading: isPromoApplyLoading } = useApplyPromo({
        options: {
            onError: (error: ApolloError) => {
                trackEvent(events.couponFail);
                setCouponStatusModal({
                    isOpen: true,
                    heading: "Failed",
                    message: error.message,
                });
            },

            onCompleted: (data: ApplyPromo) => {
                trackEvent(events.couponSuccess);
                setCouponStatusModal({
                    isOpen: true,
                    heading: "Coupon applied successfully",
                    message: data.applyPromo,
                });
            },
        },
    });

    const loading = isCardUpdateLoading || isPromoApplyLoading;

    const cardElementChanged = ({ complete, error }: any) => {
        if (!complete) {
            error ? setError(error.message) : setError(null);
            setIsSubmitDisabled(true);
        } else {
            setError(null);
            setIsSubmitDisabled(false);
        }
    };

    const updateCardInfo = async () => {
        setIsCardUpdateLoading(true);
        try {
            const { source } = await stripe.createSource({
                type: "card",
                owner: {
                    email: user?.email,
                },
            });
            await onCreateSourceID(source.id);
            if (promoCode.trim() !== "") {
                applyPromo({ promoCode });
            }
            setPromoCode("");
            onSuccess();
        } catch (err) {
            console.log(err);
            onFailure();
        }
        setIsCardUpdateLoading(false);
    };

    return (
        <Stack
            direction="vertical"
            fullWidth={true}
            gap={2}
            styles={loading ? styles.DisableStyles : {}}
        >
            <Stack direction="vertical" gap={1} fullWidth={true}>
                <Heading variant={5}>Add Credit Card</Heading>
                {error && <Alert variant="error">{error}</Alert>}
                <Container styles={styles.CardInputContainer}>
                    <CardElement onChange={cardElementChanged} />
                </Container>
            </Stack>
            <Stack direction="vertical" gap={1} fullWidth={true}>
                <Heading variant={5}>Coupon Code</Heading>
                <Input
                    placeholder="Optional"
                    value={promoCode}
                    onChange={event => setPromoCode(event.target.value)}
                />
            </Stack>
            {loading && <Spinner />}
            {!loading && (
                <Stack
                    direction="horizontal"
                    gap={1}
                    justify="flex-end"
                    fullWidth={true}
                >
                    <Button
                        styles={styles.CancelButtonStyles}
                        variant="ghost"
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        disabled={isSubmitDisabled}
                        onClick={updateCardInfo}
                        styles={styles.UpdateButtonStyles}
                    >
                        Add
                    </Button>
                </Stack>
            )}
            <Modal
                isOpen={couponStatusModal.isOpen}
                heading={couponStatusModal.heading}
                hideCloseButton={true}
                continueLabel="Okay"
                onContinue={() => setCouponStatusModal(resetStatusModalState)}
            >
                {couponStatusModal.message}
            </Modal>
        </Stack>
    );
}

export default injectStripe(UpdateCard);
